// src/pages/info/HouseDetails.jsx

import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Split from 'react-split';
import '../../CheckPage/CheckPage.css';
import '../../../components/Modals/ModalBase.css';
import './HouseDetails.css';
import Select from 'react-select';
import DeleteConfirmationModal from '../../../components/Modals/DeleteConfirmationModal';
import Modal from 'react-modal';
import API_BASE_URL from '../../../config';


Modal.setAppElement('#root');

const HouseDetails = () => {
  const location = useLocation();
  const { id } = useParams();
  const navigate = useNavigate();

  // Источник перехода: 'map' или 'db'. Берём из location.state, если есть, иначе по умолчанию 'db'
  const initialSource = location.state?.source === 'map' ? 'map' : 'db';

  // house — сам объект дома (без поля source, оно хранится отдельно)
  const [house, setHouse] = useState(location.state?.houseData || null);
  // source — из какого экрана пришли
  const [source] = useState(initialSource);
  // loading = true, если мы ждём данных с бэка; если initialState передал houseData, loading = false
  const [loading, setLoading] = useState(!location.state?.houseData);

  // Для галереи: данные серии, описание, список картинок и выбранная картинка
  const [seriesData, setSeriesData] = useState(null);
  const [descriptionParts, setDescriptionParts] = useState([]);
  const [imageOptions, setImageOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);

  // Состояния для Split-контейнера и модалок
  const [splitSizes, setSplitSizes] = useState([28, 72]);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxSrc, setLightboxSrc] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Метка для хлебных крошек: если source === 'map' → "Карта домов", иначе → "БД домов"
  const sourceLabel = source === 'map' ? 'Карта домов' : 'БД домов';

  // ─── Загрузка данных дома при монтировании ─────────────────────────────────────
  useEffect(() => {
    console.log(`[HouseDetails] Loading house with id=${id}`);
    setLoading(true);
    fetch(`${API_BASE_URL}/building/get_build_by_id/${id}`)
      .then(res => {
        if (!res.ok) throw new Error(res.statusText);
        return res.json();
      })
      .then(data => {
        console.log('[HouseDetails] House data received:', data);
        setHouse(data);
      })
      .catch(err => console.error('[HouseDetails] Error loading house:', err))
      .finally(() => {
        setLoading(false);
        console.log('[HouseDetails] Finished loading house');
      });
  }, [id]);

  // ─── После загрузки house, если есть series_id → грузим данные по серии ─────────
  useEffect(() => {
    if (!house?.series_id) return;

    console.log(`[HouseDetails] Loading series data for series_id=${house.series_id}`);
    fetch(`${API_BASE_URL}/building/series_all`)
      .then(res => {
        if (!res.ok) throw new Error(`Error loading series: ${res.status}`);
        return res.json();
      })
      .then(seriesList => {
        const found = seriesList.find(s => s.id === house.series_id);
        if (!found) {
          console.warn('[HouseDetails] Series not found:', house.series_id);
          return;
        }
        console.log('[HouseDetails] Series data:', found);
        setSeriesData(found);

        // Парсим описание: формат в базе – **Заголовок**, @@текст@@
        if (found.description) {
          const raw = found.description;
          const segments = raw
            .split('@@')
            .map(s => s.trim())
            .filter(s => s.length > 0);

          const parts = segments.map(seg => {
            if (seg.startsWith('**') && seg.endsWith('**')) {
              return { type: 'heading', text: seg.slice(2, -2).trim() };
            }
            return { type: 'paragraph', text: seg.replace(/\*\*(.*?)\*\*/, '$1').trim() };
          });

          setDescriptionParts(parts);
        }

        // Готовим массив опций для Select (галерея чертежей)
        const opts = found.images?.map((img, idx) => ({
          value: img.url,
          label: `Чертёж ${idx + 1}`,
        })) || [];

        setImageOptions(opts);
        if (opts.length > 0) {
          setSelectedOption(opts[0]);
        }
      })
      .catch(err => console.error('[HouseDetails] Error loading series:', err));
  }, [house]);

  // ─── Показываем спиннер или сообщение об отсутствии данных ────────────────────
  if (loading) {
    return <div className="house-details-page">Загрузка данных дома...</div>;
  }
  if (!house) {
    return <div className="house-details-page">Дом не найден</div>;
  }

  // ─── Открыть картинку в лайтбоксе ─────────────────────────────────────────────
  const openLightbox = url => {
    console.log('[HouseDetails] Open lightbox for image:', url);
    setLightboxSrc(url);
    setLightboxOpen(true);
  };

  // ─── Обработчик подтверждения удаления ────────────────────────────────────────
  const handleDeleteConfirm = async () => {
    console.log('[HouseDetails] handleDeleteConfirm called, attempting to delete id=', id);
    try {
      console.log('[HouseDetails] Sending DELETE request to /building/delete_build/', id);
      const response = await fetch(
        `${API_BASE_URL}/building/delete_build/${id}`,
        {
          method: 'DELETE',
          headers: {
            Accept: 'application/json',
          },
        }
        
      );

      console.log('[HouseDetails] Server response:', response);
      if (!response.ok) {
        const errorText = await response.text();
        console.error('[HouseDetails] Server returned error on delete:', errorText);
      } else {
        const resultJson = await response.json().catch(() => null);
        console.log('[HouseDetails] Successful delete response:', resultJson);

        // Сохраняем id удалённого дома и timestamp в localStorage
        const deletedHouseId = Number(id);
        const expiry = Date.now() + 60 * 60 * 1000; // 1 час
        localStorage.setItem('hiddenHouse', JSON.stringify({ id: deletedHouseId, expiry }));

        // Возвращаемся в зависимости от источника
        if (source === 'map') {
          navigate('/expenses', { state: { deletedId: deletedHouseId } });
        } else {
          navigate('/db-house', { state: { deletedId: deletedHouseId } });
        }
      }
    
    } catch (err) {
      console.error('[HouseDetails] Error deleting house:', err);
    } finally {
      console.log('[HouseDetails] Closing delete modal');
      setShowDeleteModal(false);
    }
  };

  return (
    <div className="house-details-page">
      <Split
        className="house-split"
        sizes={splitSizes}
        minSize={[450, 700]}
        maxSize={[900, Infinity]}
        gutterSize={6}
        direction="horizontal"
        cursor="col-resize"
        onDragEnd={sizes => setSplitSizes(sizes)}
      >
        {/* ───────────── ЛЕВАЯ ПАНЕЛЬ ───────────────────────────────────────── */}
        <div className="house-panel-left">
          <div
            className="breadcrumbs-fixed"
            style={{ userSelect: 'none' }}
            onMouseEnter={() =>
              document.querySelector('.breadcrumb-link')?.classList.add('hover')
            }
            onMouseLeave={() =>
              document.querySelector('.breadcrumb-link')?.classList.remove('hover')
            }
          >
            <span
              className="breadcrumb-link clickable"
              onClick={() => {
                console.log('[HouseDetails] Click on breadcrumb, source =', source);
                if (source === 'map') {
                  navigate('/expenses');
                } else {
                  navigate('/db-house');
                }
              }}
            >
              {sourceLabel}
            </span>
            <span className="breadcrumb-separator">/</span>
            <span className="breadcrumb-current">Просмотр дома</span>
          </div>

          <h2 className="house-address">{house.address}</h2>
          <p className="house-details-text">
            <strong>Серия дома:</strong> {seriesData?.name || house.series_name || '—'}
          </p>
          <p className="house-details-text">
            <strong>Количество этажей:</strong> {house.floors}
          </p>
          <p className="house-details-text">
            <strong>Количество квартир:</strong> {house.count_appartaments}
          </p>
          <p className="house-details-text">
            <strong>Год постройки:</strong> {house.construction_year} г.
          </p>

          <div className="HD-text">
            {descriptionParts.map((part, idx) =>
              part.type === 'heading' ? (
                <p key={idx}>
                  <strong>{part.text}</strong>
                </p>
              ) : (
                <p key={idx}>{part.text}</p>
              )
            )}
          </div>
        </div>

        {/* ───────────── ПРАВАЯ ПАНЕЛЬ ───────────────────────────────────────── */}
        <div className="house-panel-right">
          <div className="house-toolbar">
            <div className="drawing-select-block">
              <Select
                className="modal-select"
                classNamePrefix="modal-select"
                options={imageOptions}
                value={selectedOption}
                onChange={opt => setSelectedOption(opt)}
                placeholder="Выбор чертежа"
                menuPortalTarget={document.body}
                menuPosition="absolute"
                styles={{
                  menuPortal: base => ({ ...base, zIndex: 9999 }),
                  control: base => ({
                    ...base,
                    backgroundColor: '#15171D',
                    borderColor: '#3B3B40',
                    borderRadius: 8,
                    minHeight: 42,
                    padding: 2,
                  }),
                  singleValue: base => ({ ...base, color: '#e5e7eb' }),
                  placeholder: base => ({ ...base, color: '#8b919d' }),
                  menu: base => ({
                    ...base,
                    backgroundColor: '#2a2c33',
                    borderColor: '#3B3B40',
                  }),
                  option: (base, state) => ({
                    ...base,
                    backgroundColor: state.isFocused ? '#3B3B40' : '#2a2c33',
                    color: '#e5e7eb',
                  }),
                }}
              />
            </div>

            <div className="house-toolbar-buttons">
              <button
                className="primary-btn"
                onClick={() => {
                  console.log('[HouseDetails] Click on "Удалить запись" button');
                  setShowDeleteModal(true);
                }}
              >
                Удалить запись
              </button>
              <button
                className="primary-btn nn"
                onClick={() => {
                  console.log('[HouseDetails] Click on "Редактировать запись" button');
                  navigate('/edit-house', {
                    state: { houseData: house, source: source },
                  });
                }}
              >
                Редактировать запись
              </button>
            </div>
          </div>

          <div className="house-image-area">
            {selectedOption ? (
              <img
                src={selectedOption.value}
                alt={selectedOption.label}
                className="house-drawing"
                onClick={() => openLightbox(selectedOption.value)}
              />
            ) : imageOptions.length > 0 ? (
              <div className="gallery-container">
                {imageOptions.map(opt => (
                  <img
                    key={opt.value}
                    src={opt.value}
                    alt={opt.label}
                    className="house-drawing"
                  />
                ))}
              </div>
            ) : (
              <div className="house-image-placeholder">(Чертежи не найдены)</div>
            )}
          </div>
        </div>
      </Split>

      {/* ───────────── Лайтбокс для просмотра чертежа ───────────── */}
      <Modal
        isOpen={lightboxOpen}
        onRequestClose={() => setLightboxOpen(false)}
        className="lightbox-modal"
        overlayClassName="lightbox-overlay"
      >
        <img
          src={lightboxSrc}
          alt="Чертёж во весь экран"
          className="lightbox-image"
          onClick={() => setLightboxOpen(false)}
        />
      </Modal>

      {/* ───────────── Модалка подтверждения удаления ───────────── */}
      {showDeleteModal && (
        <DeleteConfirmationModal
          onClose={() => {
            console.log('[HouseDetails] Delete modal closed without confirmation');
            setShowDeleteModal(false);
          }}
          onConfirm={handleDeleteConfirm}
        />
      )}
    </div>
  );
};

export default HouseDetails;
